import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SlidersHorizontal } from 'lucide-react';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Skeleton,
  DistributionCard,
  useIsMobile,
} from '@hotelise/ui';
import { DateRangePicker, type DateRange } from '@hotelise/ui';
import { format, startOfMonth, endOfMonth } from 'date-fns';

import { getProperties } from 'state/modules/properties';
import { getPropertiesList } from 'state/modules/properties/selectors';
import { Property } from 'models/properties';
import { Reservation } from 'models/reservations';

import { channelsList, matchesChannelFilter } from 'utils/channels';
import { DATE_PRESETS } from 'utils/datePresets';

import { fetchReservations } from './fetchAllReservations';
import { mergeGuests, buildCountryDistribution } from './guestUtils';
import GuestArrivalsChart from './GuestArrivalsChart';
import GuestRepeatRate from './GuestRepeatRate';
import GuestLeadTimeChart from './GuestLeadTimeChart';
import PeakArrivalDays from './PeakArrivalDays';

// ── Helpers ──────────────────────────────────────────────────────────

const getDefaultDateRange = (): DateRange => {
  const now = new Date();
  return { from: startOfMonth(now), to: endOfMonth(now) };
};

function toISODate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

// ── Types ────────────────────────────────────────────────────────────

export type GuestsPageViewMode = 'admin' | 'owner';

interface GuestsPageProps {
  /**
   * Controls which sections are visible:
   * - 'admin': full page with guest table, search, detail sheet, reservation drill-down
   * - 'owner': widgets only (arrivals, distribution, repeat rate, peak days, lead time)
   *            Guest table hidden for data privacy.
   */
  viewMode?: GuestsPageViewMode;
}

// ── Component ────────────────────────────────────────────────────────

const GuestsPage = ({ viewMode: _viewMode = 'owner' }: GuestsPageProps): JSX.Element => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const properties: Property[] = useSelector(getPropertiesList);

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);

  const [channelFilter, setChannelFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const fetchGenRef = useRef(0);

  // Load properties on mount
  useEffect(() => {
    dispatch(getProperties());
  }, [dispatch]);

  // Fetch reservations whenever the date range changes
  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;

    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    let cancelled = false;

    setLoading(true);
    fetchReservations({
      dateFrom: toISODate(dateRange.from),
      dateTo: toISODate(dateRange.to),
      isCancelled: () => cancelled,
    })
      .then((res) => {
        if (!cancelled && gen === fetchGenRef.current) setReservations(res);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to fetch reservations for guests page:', err);
      })
      .finally(() => {
        if (!cancelled && gen === fetchGenRef.current) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dateRange]);

  // Build properties map
  const propertiesMap = useMemo(() => {
    const map: Record<string, Property> = {};
    properties.forEach((p) => { map[p.id] = p; });
    return map;
  }, [properties]);

  // Filter raw reservations by channel/property BEFORE merging
  const filteredReservations = useMemo(() => {
    let list = reservations;

    if (channelFilter !== 'all') {
      list = list.filter((r) => matchesChannelFilter(r.channel, channelFilter));
    }

    if (propertyFilter !== 'all') {
      list = list.filter((r) => String(r.listing?.property_id) === propertyFilter);
    }

    return list;
  }, [reservations, channelFilter, propertyFilter]);

  // ALL merged guests — used by widgets (arrivals, country dist, repeat rate, lead time)
  const allGuests = useMemo(
    () => mergeGuests(filteredReservations, propertiesMap),
    [filteredReservations, propertiesMap],
  );

  // Country distribution from ALL guests
  const countryDist = useMemo(
    () => buildCountryDistribution(allGuests),
    [allGuests],
  );

  const channelOptions = channelsList;

  const hasFilters = channelFilter !== 'all' || propertyFilter !== 'all';
  const activeFilterCount = (channelFilter !== 'all' ? 1 : 0) + (propertyFilter !== 'all' ? 1 : 0);

  const handleClearFilters = useCallback(() => {
    setChannelFilter('all');
    setPropertyFilter('all');
  }, []);

  const handlePresetClick = useCallback((preset: typeof DATE_PRESETS[number]) => {
    setActivePreset(preset.label);
    setDateRange(preset.getRange());
  }, []);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setActivePreset(null);
    setDateRange(range);
  }, []);

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <p className="text-[26px] font-bold text-foreground m-0">Guests</p>
      <p className="text-sm text-muted-foreground py-[5px] pb-[10px] m-0">
        Guest analytics and insights.
      </p>

      {/* Filters — Mobile */}
      {isMobile ? (
        <section aria-label="Filters" className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-4">
          {/* Date Presets */}
          <div className="flex flex-wrap gap-1.5">
            {DATE_PRESETS.map((preset) => (
              <Badge
                key={preset.label}
                variant={activePreset === preset.label ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Badge>
            ))}
          </div>

          {/* Date Range + Filter Toggle */}
          <div className="flex items-center gap-2">
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              numberOfMonths={1}
              className="flex-1 w-auto text-xs h-8"
            />
            <Button
              variant={filtersOpen ? 'secondary' : 'outline'}
              size="sm"
              className="h-8 px-2.5 shrink-0"
              onClick={() => setFiltersOpen(!filtersOpen)}
              aria-label="Toggle filters"
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {activeFilterCount > 0 && (
                <span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full h-4 w-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Expandable filters */}
          {filtersOpen && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All channels</SelectItem>
                    {channelOptions.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All properties</SelectItem>
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  Reset
                </Button>
              )}
            </div>
          )}
        </section>
      ) : (
        /* Filters — Desktop */
        <section aria-label="Filters" className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-4">
          {/* Date Presets */}
          <div className="flex flex-wrap gap-1.5">
            {DATE_PRESETS.map((preset) => (
              <Badge
                key={preset.label}
                variant={activePreset === preset.label ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Badge>
            ))}
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground block">Date range</span>
              <div>
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  numberOfMonths={2}
                />
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground" id="channel-filter-label">Channel</span>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-[130px]" aria-labelledby="channel-filter-label"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {channelOptions.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground" id="property-filter-label">Property</span>
              <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                <SelectTrigger className="w-[160px]" aria-labelledby="property-filter-label"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Reset
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Loading state — skeleton placeholders matching widget layout */}
      {loading ? (
        <>
          {/* Arrivals chart + Country distribution skeleton */}
          <section className="mb-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] min-w-0">
            <div className="bg-card rounded-lg p-4 md:p-6 border">
              <div className="flex items-center gap-3 mb-6">
                <Skeleton className="h-9 w-9 rounded-md" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-9 w-9 rounded-md" />
                <div className="ml-2">
                  <Skeleton className="h-5 w-32 mb-1" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
              <Skeleton className="h-[400px] w-full rounded" />
            </div>
            <div className="bg-card rounded-lg p-4 md:p-6 border">
              <Skeleton className="h-5 w-40 mb-4" />
              {['skel-dist-1', 'skel-dist-2', 'skel-dist-3', 'skel-dist-4', 'skel-dist-5'].map((id) => (
                <div key={id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-4 w-8" />
                </div>
              ))}
            </div>
          </section>

          {/* Repeat Rate + Peak Arrivals + Lead Time skeleton */}
          <section className="mb-6 grid gap-4 grid-cols-1 md:grid-cols-3 min-w-0">
            <div className="bg-card rounded-lg border p-4 md:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-4 w-28" />
              </div>
              <Skeleton className="h-9 w-20 mb-1" />
              <Skeleton className="h-4 w-36 mb-3" />
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
            <div className="bg-card rounded-lg border p-4 md:p-6">
              <Skeleton className="h-4 w-32 mb-1" />
              <Skeleton className="h-3 w-44 mb-4" />
              <Skeleton className="h-[260px] w-full rounded" />
            </div>
            <div className="bg-card rounded-lg border p-4 md:p-6">
              <Skeleton className="h-4 w-24 mb-1" />
              <Skeleton className="h-3 w-32 mb-4" />
              <Skeleton className="h-[260px] w-full rounded" />
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Analytics — Arrivals + Country Distribution (ALL guests) */}
          <section className="mb-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] min-w-0">
            <GuestArrivalsChart guests={allGuests} dateRange={dateRange} />
            <DistributionCard
              title="Country Distribution"
              items={countryDist}
              unit="guests"
            />
          </section>

          {/* Widgets — Repeat Rate + Peak Arrivals + Lead Time (ALL guests) */}
          <section className="mb-6 grid gap-4 grid-cols-1 md:grid-cols-3 min-w-0">
            <GuestRepeatRate guests={allGuests} />
            <PeakArrivalDays guests={allGuests} />
            <GuestLeadTimeChart guests={allGuests} dateRange={dateRange} />
          </section>
        </>
      )}
    </div>
  );
};

export default GuestsPage;
